/**
 * Competition entry form (sections/competition-entry.liquid).
 * Client-side validation, error summary, closed state and submission to Klaviyo's client APIs
 * (public site key only). Nothing is stored in localStorage.
 */
(() => {
  const MESSAGES = {
    name: 'Enter your full name.',
    emailEmpty: 'Enter your email address.',
    emailInvalid: 'Enter an email address in the right format, like name@example.co.uk.',
    barcodeEmpty: 'Enter the 13-digit barcode number from your pack.',
    barcodeLength: (n) => `The barcode number has 13 digits. You've entered ${n}.`,
    barcodeInvalid: "That barcode number isn't valid. Check the 13 digits under the bars on your pack.",
    barcodeIneligible: "That barcode isn't one of the TrueStart packs in this competition. Check the number on your pack.",
    retailer: 'Choose where you bought your coffee.',
    previous: 'Please choose Yes or No.',
    terms: 'Agree to the competition terms to enter.',
    network: "Sorry, we couldn't send your entry. Please check your connection and try again.",
  };

  const FIELD_ORDER = ['name', 'email', 'barcode', 'retailer', 'previous', 'terms'];
  // Readable answers for Klaviyo, rather than the raw radio values
  const PREVIOUS_LABELS = { yes: 'Yes', no: 'No' };
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const KLAVIYO_URL = 'https://a.klaviyo.com/client/';
  const REQUEST_TIMEOUT = 15000;

  const scrollBehavior = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  // GS1 EAN-13 check digit: weights 1,3,1,3... over the first 12 digits
  const isValidEan13 = (digits) => {
    if (!/^\d{13}$/.test(digits)) return false;
    let sum = 0;
    for (let i = 0; i < 12; i++) sum += Number(digits[i]) * (i % 2 ? 3 : 1);
    return (10 - (sum % 10)) % 10 === Number(digits[12]);
  };

  class CompetitionEntry {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-comp-form]');
      this.closedPanel = root.querySelector('[data-comp-closed]');
      if (!this.form) return;

      this.config = JSON.parse(root.querySelector('[data-comp-config]').textContent);
      this.eligible = (this.config.eligibleBarcodes || '')
        .split(/[\s,;]+/)
        .map((code) => code.replace(/\D/g, ''))
        .filter(Boolean);

      this.success = root.querySelector('[data-comp-success]');
      this.summary = root.querySelector('[data-comp-summary]');
      this.summaryTitle = root.querySelector('[data-comp-summary-title]');
      this.summaryList = root.querySelector('[data-comp-summary-list]');
      this.submitButton = root.querySelector('[data-comp-submit]');
      this.submitLabel = this.submitButton.textContent.trim();
      this.submitError = root.querySelector('[data-comp-submit-error]');

      const el = (name) => this.form.elements.namedItem(name);
      this.inputs = {
        name: el('name'),
        email: el('email'),
        barcode: el('barcode'),
        retailer: el('retailer'),
        previous: this.form.querySelector('input[name="previous_purchase"]'),
        terms: el('terms'),
        optTruestart: el('opt_truestart'),
        optDelonghi: el('opt_delonghi'),
        honeypot: el('website'),
      };
      // Radio groups mark every option invalid; the first option is the summary link and focus target
      this.groups = {
        previous: [...this.form.querySelectorAll('input[name="previous_purchase"]')],
      };

      this.tried = false;
      this.errors = {};
      this.submitting = false;

      // Pages are cached, so the Liquid closed check can be out of date
      if (this.isClosed()) {
        this.showClosed();
        return;
      }

      this.bind();
      this.handleArrival();
    }

    isClosed() {
      return this.config.closeMs > 0 && Date.now() >= this.config.closeMs;
    }

    /* ---------- Events ---------- */

    bind() {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.onSubmit();
      });
      this.form.addEventListener('input', () => this.liveValidate());
      this.form.addEventListener('change', () => this.liveValidate());

      const barcode = this.inputs.barcode;
      barcode.addEventListener('input', () => {
        const digits = barcode.value.replace(/\D/g, '').slice(0, 13);
        if (digits !== barcode.value) barcode.value = digits;
      });
      // maxlength would cut a pasted "5 012345 678900" short, so strip spaces first
      barcode.addEventListener('paste', (event) => {
        const text = event.clipboardData ? event.clipboardData.getData('text') : '';
        event.preventDefault();
        barcode.setRangeText(text.replace(/\D/g, ''), barcode.selectionStart, barcode.selectionEnd, 'end');
        barcode.value = barcode.value.slice(0, 13);
        barcode.dispatchEvent(new Event('input', { bubbles: true }));
      });

      this.summaryList.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-field]');
        if (!link) return;
        event.preventDefault();
        this.focusField(link.dataset.field);
      });

      this.root.querySelector('[data-comp-reset]').addEventListener('click', () => this.reset());

      // In-page links to the form (e.g. the hero "Enter the draw" button) move focus to the first field
      if (this.config.anchorId) {
        document.addEventListener('click', (event) => {
          const link = event.target.closest('a[href*="#"]');
          if (!link || link.hash !== `#${this.config.anchorId}`) return;
          if (link.pathname !== window.location.pathname) return;
          event.preventDefault();
          history.pushState(null, '', link.hash);
          this.goToForm();
        });
        window.addEventListener('hashchange', () => this.handleArrival());
      }

      // Re-check the closing time if the tab has been left open
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.isClosed()) this.showClosed();
      });
    }

    /* ---------- Navigation / focus ---------- */

    handleArrival() {
      if (!this.config.anchorId || window.location.hash !== `#${this.config.anchorId}`) return;
      if (window.Shopify && window.Shopify.designMode) return;
      if (document.readyState === 'complete') this.focusFirstField();
      else window.addEventListener('load', () => requestAnimationFrame(() => this.focusFirstField()), { once: true });
    }

    goToForm() {
      const target = this.root.querySelector(`#${CSS.escape(this.config.anchorId)}`) || this.root;
      target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      this.focusFirstField();
    }

    focusFirstField() {
      if (!this.closedPanel.hidden) this.closedPanel.focus({ preventScroll: true });
      else if (this.form.hidden) this.success.querySelector('[data-comp-success-heading]').focus({ preventScroll: true });
      else this.inputs.name.focus({ preventScroll: true });
    }

    focusField(key) {
      const target = this.inputs[key];
      if (!target) return;
      const field = target.closest('[data-comp-field]') || target;
      field.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
      target.focus({ preventScroll: true });
    }

    /* ---------- Validation ---------- */

    validate() {
      const errors = {};
      const name = this.inputs.name.value.trim();
      const email = this.inputs.email.value.trim();
      const barcode = this.inputs.barcode.value.replace(/\D/g, '');

      if (!name) errors.name = MESSAGES.name;

      if (!email) errors.email = MESSAGES.emailEmpty;
      else if (!EMAIL_RE.test(email)) errors.email = MESSAGES.emailInvalid;

      if (!barcode) errors.barcode = MESSAGES.barcodeEmpty;
      else if (barcode.length !== 13) errors.barcode = MESSAGES.barcodeLength(barcode.length);
      else if (!isValidEan13(barcode)) errors.barcode = MESSAGES.barcodeInvalid;
      else if (this.eligible.length && !this.eligible.includes(barcode)) errors.barcode = MESSAGES.barcodeIneligible;

      if (!this.inputs.retailer.value) errors.retailer = MESSAGES.retailer;
      if (!this.form.querySelector('input[name="previous_purchase"]:checked')) errors.previous = MESSAGES.previous;
      if (!this.inputs.terms.checked) errors.terms = MESSAGES.terms;

      return errors;
    }

    liveValidate() {
      if (!this.tried) return;
      this.errors = this.validate();
      this.renderFieldErrors();
      this.pruneSummary();
    }

    renderFieldErrors() {
      FIELD_ORDER.forEach((key) => {
        const message = this.errors[key];
        const control = this.inputs[key];
        const errorEl = this.root.querySelector(`[data-comp-error="${key}"]`);
        if (!control || !errorEl) return;

        errorEl.querySelector('[data-comp-error-text]').textContent = message || '';
        errorEl.hidden = !message;

        (this.groups[key] || [control]).forEach((input) => {
          const describedBy = (input.getAttribute('aria-describedby') || '')
            .split(' ')
            .filter((id) => id && id !== errorEl.id);
          if (message) {
            describedBy.push(errorEl.id);
            input.setAttribute('aria-invalid', 'true');
          } else {
            input.removeAttribute('aria-invalid');
          }
          if (describedBy.length) input.setAttribute('aria-describedby', describedBy.join(' '));
          else input.removeAttribute('aria-describedby');
        });
      });
    }

    summaryHeading(count) {
      return count === 1 ? 'There is 1 problem with your entry' : `There are ${count} problems with your entry`;
    }

    renderSummary() {
      const keys = FIELD_ORDER.filter((key) => this.errors[key]);
      this.summaryList.replaceChildren(
        ...keys.map((key) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${this.inputs[key].id}`;
          link.dataset.field = key;
          link.textContent = this.errors[key];
          item.appendChild(link);
          return item;
        })
      );
      this.summaryTitle.textContent = this.summaryHeading(keys.length);
      this.summary.hidden = keys.length === 0;
    }

    // While typing, only remove fixed items so the alert isn't re-announced on every keystroke
    pruneSummary() {
      if (this.summary.hidden) return;
      this.summaryList.querySelectorAll('a[data-field]').forEach((link) => {
        if (!this.errors[link.dataset.field]) link.parentElement.remove();
        else link.textContent = this.errors[link.dataset.field];
      });
      const remaining = this.summaryList.children.length;
      if (remaining === 0) this.summary.hidden = true;
      else this.summaryTitle.textContent = this.summaryHeading(remaining);
    }

    /* ---------- Submit ---------- */

    onSubmit() {
      if (this.submitting || this.submitButton.disabled) return;
      this.submitError.hidden = true;

      if (this.isClosed()) {
        this.showClosed();
        return;
      }

      this.tried = true;
      this.errors = this.validate();
      this.renderFieldErrors();
      this.renderSummary();

      if (Object.keys(this.errors).length) {
        this.summary.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
        this.summary.focus({ preventScroll: true });
        return;
      }

      this.send();
    }

    collect() {
      const fullName = this.inputs.name.value.trim().replace(/\s+/g, ' ');
      const [firstName, ...rest] = fullName.split(' ');
      const params = new URLSearchParams(window.location.search);
      const utms = {};
      UTM_KEYS.forEach((key) => {
        if (params.get(key)) utms[key] = params.get(key);
      });
      const previous = this.form.querySelector('input[name="previous_purchase"]:checked');

      return {
        firstName,
        lastName: rest.join(' '),
        email: this.inputs.email.value.trim().toLowerCase(),
        barcode: this.inputs.barcode.value.replace(/\D/g, ''),
        retailer: this.inputs.retailer.value,
        previousPurchase: previous ? PREVIOUS_LABELS[previous.value] : '',
        optTruestart: this.inputs.optTruestart.checked,
        optDelonghi: this.inputs.optDelonghi.checked,
        terms: this.inputs.terms.checked,
        utms,
      };
    }

    async send() {
      const data = this.collect();

      // Bots fill the hidden field: show success but send nothing
      if (this.inputs.honeypot.value) {
        this.showSuccess(data);
        return;
      }

      if (!this.config.publicKey) {
        this.showSubmitError();
        return;
      }

      this.setBusy(true);
      const submittedAt = new Date().toISOString();

      try {
        // Only a 202 from Create Client Event counts as an entry
        await this.request('events', this.eventPayload(data, submittedAt));
      } catch (error) {
        console.error('[competition-entry] Entry not sent', error);
        this.setBusy(false);
        this.showSubmitError();
        return;
      }

      // The entry is recorded, so a failed list subscription doesn't block the success message
      if (data.optTruestart && this.config.listId) {
        try {
          await this.request('subscriptions', this.subscriptionPayload(data));
        } catch (error) {
          console.error('[competition-entry] TrueStart subscription not sent', error);
        }
      }

      this.setBusy(false);
      this.showSuccess(data);
    }

    eventPayload(data, submittedAt) {
      const competition = this.config.competition;
      // Research answers, not consent: saved on every entrant's profile for segments and reports
      const profileProperties = {
        tried_truestart_before: data.previousPurchase,
        tried_truestart_before_source: competition,
        coffee_retailer: data.retailer,
        coffee_retailer_source: competition,
      };

      // De'Longhi consent is stored as a profile property, not a list subscription.
      // Only a "yes" is written, so an unticked box never overwrites an earlier consent.
      if (data.optDelonghi) {
        profileProperties.delonghi_marketing_consent = true;
        profileProperties.delonghi_marketing_consent_timestamp = submittedAt;
        profileProperties.delonghi_marketing_consent_source = competition;
      }

      const lastName = data.lastName ? { last_name: data.lastName } : {};

      return {
        data: {
          type: 'event',
          attributes: {
            properties: {
              competition_id: competition,
              barcode: String(data.barcode),
              retailer: data.retailer,
              previous_purchase: data.previousPurchase,
              truestart_marketing_consent: data.optTruestart,
              delonghi_marketing_consent: data.optDelonghi,
              consent_timestamp: submittedAt,
              terms_accepted: data.terms,
              terms_version: this.config.termsVersion,
              page_url: window.location.origin + window.location.pathname,
              ...data.utms,
            },
            time: submittedAt,
            unique_id: `${competition}:${data.email}:${data.barcode}`,
            metric: {
              data: { type: 'metric', attributes: { name: 'Competition Entry' } },
            },
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: data.email,
                  first_name: data.firstName,
                  ...lastName,
                  properties: profileProperties,
                },
              },
            },
          },
        },
      };
    }

    subscriptionPayload(data) {
      return {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: `Competition: ${this.config.competition}`,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: data.email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: this.config.listId } },
          },
        },
      };
    }

    async request(endpoint, payload) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
      try {
        const response = await fetch(
          `${KLAVIYO_URL}${endpoint}?company_id=${encodeURIComponent(this.config.publicKey)}`,
          {
            method: 'POST',
            headers: {
              accept: 'application/vnd.api+json',
              'content-type': 'application/vnd.api+json',
              revision: this.config.revision,
            },
            body: JSON.stringify(payload),
            signal: controller.signal,
          }
        );
        if (response.status !== 202) throw new Error(`Klaviyo ${endpoint} responded ${response.status}`);
      } finally {
        clearTimeout(timer);
      }
    }

    setBusy(busy) {
      this.submitting = busy;
      this.submitButton.disabled = busy;
      this.submitButton.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.submitButton.textContent = busy ? 'Sending your entry…' : this.submitLabel;
      this.form.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    showSubmitError() {
      this.submitError.textContent = MESSAGES.network;
      this.submitError.hidden = false;
      this.submitButton.focus();
    }

    /* ---------- States ---------- */

    showSuccess(data) {
      this.root.querySelectorAll('[data-comp-first-name]').forEach((el) => {
        el.textContent = data.firstName || 'there';
      });
      this.form.hidden = true;
      this.success.hidden = false;
      const panel = this.root.querySelector('[data-comp-panel]');
      if (panel.getBoundingClientRect().top < 0) panel.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      this.success.querySelector('[data-comp-success-heading]').focus({ preventScroll: true });
    }

    reset() {
      // Keep name, email and retailer; clear the pack, confirmations and consents so each entry is ticked afresh
      this.inputs.barcode.value = '';
      ['terms', 'optTruestart', 'optDelonghi'].forEach((key) => {
        this.inputs[key].checked = false;
      });
      this.tried = false;
      this.errors = {};
      this.renderFieldErrors();
      this.summary.hidden = true;
      this.submitError.hidden = true;

      this.success.hidden = true;
      this.form.hidden = false;
      this.inputs.barcode.focus();
    }

    showClosed() {
      this.form.hidden = true;
      if (this.success) this.success.hidden = true;
      this.closedPanel.hidden = false;
    }
  }

  const init = (scope) => {
    scope.querySelectorAll('[data-comp-entry]').forEach((root) => {
      if (root.dataset.compReady) return;
      root.dataset.compReady = 'true';
      new CompetitionEntry(root);
    });
  };

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
